import { Vue,Component, Prop, Emit, Watch} from 'vue-property-decorator';
import Modal from '@/assets/common/Modal/Modal.vue'
import { toCamel } from 'snake-camel';


type Fertilizer = {
    id: string;
    pdFertilizerId?: string | null;
    name: string;
    maker: string | null;
    chemRetio: number | null;
    unitName: string | null;
    warehouseId : string | null;
    warehouseName : string | null;
    lastUsed: Date | null;
    ownerClass?: number;
}

type Sel = {
    id: string;
    warehouseId : string;
}

type FertilizersGet = {
    recommendations: Fertilizer[];
    histories: Fertilizer[];
    fertilizersFromWarehouseMaster: Fertilizer[];
    fertilizersFromMaster: Fertilizer[];
}

type Warehouse = {
    warehouseId: string;
    warehouseName: string;
}

type FertilizerSortTab = 'recommendation' | 'history' | 'warehouse-master' | 'fertilizer-master'
type FertilizerSortField = 'name' | 'lastUsed'
type FertilizerSortState = {
    field: FertilizerSortField;
    order: 'asc' | 'desc';
}

const createFertilizerSortStates = (): Record<FertilizerSortTab, FertilizerSortState> => ({
    recommendation: { field: 'name', order: 'asc' },
    history: { field: 'name', order: 'asc' },
    'warehouse-master': { field: 'name', order: 'asc' },
    'fertilizer-master': { field: 'name', order: 'asc' }
});

@Component({
    components: {
        Modal
    }
})
export default class FertilizerSelectionModal extends Vue {

    @Prop() 
    isShowFertilizerSelectionModal!: boolean

    @Prop() 
    tenantId!: string

    @Prop() 
    pdTenantId!: string

    @Prop() 
    sitemId!: string;

    @Prop()
    initialValue!: Sel[];

    @Prop()
    isWorkRecord!:boolean

    @Prop()
    isCultivationHistory!:boolean

    @Prop()
    isFertRecommendedOnly!: boolean

    selectedTab: FertilizerSortTab = 'recommendation'
    isSpinnerLoading = false;
    warehouses: Warehouse[] = [];
    selectedWarehouseId = null;
    recommendations: Fertilizer[] = [];
    filteredRecommendations: Fertilizer[] = [];
    histories: Fertilizer[] = [];
    filteredHistories: Fertilizer[] = [];
    fertilizersFromWarehouseMaster: Fertilizer[] = [];
    filteredFertilizersFromWarehouseMaster: Fertilizer[] = [];
    fertilizersFromMaster: Fertilizer[] = [];
    filteredFertilizersFromMaster: Fertilizer[] = [];
    searchRecommendation = ''
    searchHistory = ''
    searchWarehouseMaster = ''
    searchFertilizerMaster = ''
    selectedFertilizers: Fertilizer[] = []
    checkedFertilizers:any []= []
    selectedFertilizerKeys: Set<string> = new Set();
    sortStates: Record<FertilizerSortTab, FertilizerSortState> = createFertilizerSortStates();

    selectTab(tab: FertilizerSortTab): void {
        this.selectedTab = tab
    }

    isSortActive(tab: FertilizerSortTab, fieldName: FertilizerSortField): boolean {
        return this.sortStates[tab].field === fieldName;
    }

    isSortAscending(tab: FertilizerSortTab, fieldName: FertilizerSortField): boolean {
        return this.isSortActive(tab, fieldName) && this.sortStates[tab].order === 'asc';
    }

    isSortDescending(tab: FertilizerSortTab, fieldName: FertilizerSortField): boolean {
        return this.isSortActive(tab, fieldName) && this.sortStates[tab].order === 'desc';
    }

    private getTargetList(tab: FertilizerSortTab): Fertilizer[] {
        return tab === 'recommendation'
            ? this.filteredRecommendations
            : tab === 'history'
                ? this.filteredHistories
                : tab === 'warehouse-master'
                    ? this.filteredFertilizersFromWarehouseMaster
                    : this.filteredFertilizersFromMaster;
    }

    private compareFertilizers(a: Fertilizer, b: Fertilizer, fieldName: FertilizerSortField, isAscending: boolean): number {
            const aValue = a[fieldName];
            const bValue = b[fieldName];
            if (aValue === null && bValue === null) return 0;
            if (aValue === null) return 1;
            if (bValue === null) return -1;

            if (fieldName === 'lastUsed') {
                const aTime = new Date(aValue as Date).getTime();
                const bTime = new Date(bValue as Date).getTime();
                return isAscending ? aTime - bTime : bTime - aTime;
            }

            return isAscending
                ? String(aValue).localeCompare(String(bValue), 'ja')
                : String(bValue).localeCompare(String(aValue), 'ja');
    }

    private applySort(tab: FertilizerSortTab): void {
        const { field, order } = this.sortStates[tab];
        this.getTargetList(tab).sort((a, b) => this.compareFertilizers(a, b, field, order === 'asc'));
    }

    sortFertilizers(tab: FertilizerSortTab, fieldName: FertilizerSortField): void {
        const currentState = this.sortStates[tab];
        this.sortStates[tab] = {
            field: fieldName,
            order: currentState.field === fieldName && currentState.order === 'asc' ? 'desc' : 'asc'
        };
        this.applySort(tab);
    }

    get pesticidePlanProducer() : boolean {
        return this.$store.getters.loginUser.pesticidePlanProducer;
    }

    get isPesticidePlan() : boolean {
        return this.$store.getters.loginUser.isPesticidePlan;
    }

     get isVegepaletteUser() : boolean {
        return !this.$store.getters.loginUser.pesticidePlanProducer && !this.$store.getters.loginUser.isPesticidePlan;
    }

    @Watch('isShowFertilizerSelectionModal')
    onModalOpen(val: boolean) {
        this.resetModal();
        if (val) {
            this.selectedFertilizerKeys = new Set(
                this.initialValue.map(f => `${f.id}_${f.warehouseId}`)
            );
            this.getFertilizers();
        }
    }

    async getFertilizers() {
        if(!this.isWorkRecord){
        if (!this.tenantId || !this.pdTenantId || !this.sitemId) return;
        }
        try {
            this.isSpinnerLoading = true;
            const sitemId = this.sitemId  ? this.sitemId : null ;
            const camelCasedResponse = toCamel(await this.$apiClient.doRequest('get', `/fertilizers/${this.tenantId || null}/${this.pdTenantId}/${sitemId}?is_cultivation_history=${!this.isWorkRecord}`)) as FertilizersGet;
            this.recommendations = camelCasedResponse.recommendations;
            this.filteredRecommendations = camelCasedResponse.recommendations;
            this.histories = camelCasedResponse.histories;
            this.filteredHistories = camelCasedResponse.histories;
            this.fertilizersFromWarehouseMaster = camelCasedResponse.fertilizersFromWarehouseMaster;
            this.filteredFertilizersFromWarehouseMaster = [];
            this.warehouses = [...new Map(camelCasedResponse.fertilizersFromWarehouseMaster
                .filter(f => f.warehouseId !== null && f.warehouseName !== null)
                .map(f => [f.warehouseId as string, { warehouseId: f.warehouseId as string, warehouseName: f.warehouseName as string }])
            ).values()];
            this.fertilizersFromMaster = camelCasedResponse.fertilizersFromMaster;
            this.filteredFertilizersFromMaster = camelCasedResponse.fertilizersFromMaster;
            this.applySort('recommendation');
            this.applySort('history');
            this.applySort('fertilizer-master');
        } catch {
            this.isSpinnerLoading = false;
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isSpinnerLoading = false;
        }
    }

    @Emit('save')
    save() {
        this.$emit('update:isShowFertilizerSelectionModal', false);
        const seen = new Set();
        const existingFromInitial: Fertilizer[] = (this.initialValue || []).map(iv => ({
            id: iv.id,
            name: '',
            maker:'',
            unitName: '',
            chemRetio : null,
            warehouseId: iv.warehouseId ?? null,
            warehouseName: null,
            lastUsed: null
        }));
    
        this.selectedFertilizers = [
            ...this.recommendations,
            ...this.histories,
            ...this.fertilizersFromWarehouseMaster,
            ...this.fertilizersFromMaster,
            ...existingFromInitial
        ].filter(f => {
            const key = this.getFertilizerKey(f);
            return this.selectedFertilizerKeys.has(key) && !seen.has(key) && seen.add(key);
        });
        return [...this.selectedFertilizers];
    }

    getFertilizerKey = (fertilizer: Fertilizer): string => `${fertilizer.id}_${fertilizer.warehouseId ?? null}`;

    toggleFertilizerSelection(fertilizer: Fertilizer) {
        this.checkedFertilizers.push(fertilizer);
        const key = this.getFertilizerKey(fertilizer);
        if (this.selectedFertilizerKeys.has(key)) {
            this.selectedFertilizerKeys.delete(key);
        } else {
            this.selectedFertilizerKeys.add(key);
        }
    }

    cancel() {
        this.$emit('update:isShowFertilizerSelectionModal', false);
    }

    resetModal () {
        this.selectedTab = this.isWorkRecord ? 'warehouse-master' : 'recommendation';
        this.recommendations = [];
        this.filteredRecommendations = [];
        this.histories = [];
        this.filteredHistories = [];
        this.fertilizersFromWarehouseMaster = [];
        this.filteredFertilizersFromWarehouseMaster = [];
        this.warehouses = [];
        this.fertilizersFromMaster = [];
        this.filteredFertilizersFromMaster = [];
        this.searchRecommendation = '';
        this.searchHistory = '';
        this.searchWarehouseMaster = '';
        this.searchFertilizerMaster = '';
        this.warehouses = [];
        this.selectedWarehouseId = null;
        this.selectedFertilizerKeys = new Set();
        this.selectedFertilizers = [];
        this.sortStates = createFertilizerSortStates();
    }

    @Watch('searchRecommendation')
    doSearchRecommendation() {
        if (this.searchRecommendation) {
            const keyword = this.searchRecommendation;
            this.filteredRecommendations = this.recommendations.filter(fertilizer =>
                fertilizer.name.includes(keyword)
            );
        } else {
            this.filteredRecommendations = this.recommendations;
        }
        this.applySort('recommendation');
    }

    @Watch('searchHistory')
    doSearchHistory() {
        if (this.searchHistory) {
            const keyword = this.searchHistory;
            this.filteredHistories = this.histories.filter(fertilizer =>
                fertilizer.name.includes(keyword)
            );
        } else {
            this.filteredHistories = this.histories;
        }
        this.applySort('history');
    }

    @Watch('searchFertilizerMaster')
    doSearchFertilizerMaster() {
        if (this.searchFertilizerMaster) {
            const keyword = this.searchFertilizerMaster;
            this.filteredFertilizersFromMaster = this.fertilizersFromMaster.filter(fertilizer =>
                fertilizer.name.includes(keyword)
            );
        } else {
            this.filteredFertilizersFromMaster = this.fertilizersFromMaster;
        }
        this.applySort('fertilizer-master');
    }

    @Watch('searchWarehouseMaster')
    doSearchWarehouseMaster() {
        if (this.searchWarehouseMaster) {
            const keyword = this.searchWarehouseMaster;
            this.filteredFertilizersFromWarehouseMaster = this.fertilizersFromWarehouseMaster.filter(fertilizer =>
                fertilizer.name.includes(keyword) &&
                fertilizer.warehouseId === this.selectedWarehouseId
            );
        } else {
            this.filteredFertilizersFromWarehouseMaster = this.fertilizersFromWarehouseMaster.filter(fertilizer =>
                fertilizer.warehouseId === this.selectedWarehouseId
            );
        }
        this.applySort('warehouse-master');
    }

    @Watch('selectedWarehouseId')
    filterWarehouseFertilizers() {
        if (this.selectedWarehouseId) {
            this.searchWarehouseMaster = '';
            this.filteredFertilizersFromWarehouseMaster = this.fertilizersFromWarehouseMaster.filter(fertilizer =>
                fertilizer.warehouseId === this.selectedWarehouseId
            );
            this.applySort('warehouse-master');
        } else {
            this.filteredFertilizersFromWarehouseMaster = []
        }
    }
}
